import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, ElementRef, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CompositeKey, Metadata, RunView, UserInfo } from '@memberjunction/core';
import { GraphQLActionClient, GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import { AttachmentDrawerComponent } from '../message-attachments/attachment-drawer.component';
import { MessageAttachmentChip } from '../message-attachments/message-attachments.component';

/** Default environment ID seeded by MJ. Matches __mj.Environment.Default. */
const DEFAULT_ENVIRONMENT_ID = 'F51358F3-9447-4176-B313-BF8025FD8D09';

/** The Channel Message Notes columns the notes card reads (AuthorPerson = CodeGen's name join for AuthorPersonID). */
type MessageNoteRow = Pick<IzzyChannelMessageNoteEntity, 'ID' | 'Body' | 'AuthorEmail' | 'AuthorPerson' | '__mj_CreatedAt'>;
import { IzzyChannelMessageEntity, IzzyChannelMessageNoteEntity, ChannelMessageEntityExtendedClient, IzzyMetadataEngine, IzzyPayload } from 'mj_generatedentities';
import { NavigationService } from '@memberjunction/ng-shared';
import { marked } from 'marked';
import * as HtmlDiffModule from 'htmldiff-js';
import { MJAIAgentRunEntity } from '@memberjunction/core-entities';
import { categorizeError, FriendlyError } from '../../utils/error-categorizer';
import { DownloadBase64File } from '../../utils/download';
import { IzzyTranslationService, TranslatableField } from '../../services/izzy-translation.service';
const HtmlDiff = HtmlDiffModule.default;

/** Which of the viewer's two text panes a translation applies to. */
type TranslationPane = 'inbound' | 'draft';

/**
 * Per-pane translate state. 'picking' shows the language chooser, 'shown' swaps the pane's text
 * for the translation (the original is one click away), 'idle' is the untouched pane.
 */
interface PaneTranslation {
  status: 'idle' | 'picking' | 'loading' | 'shown' | 'error';
  language: string;
  translatedText: string;
  translatedSubject: string | null;
  detectedLanguage: string;
  error: string;
}

const IDLE_TRANSLATION: PaneTranslation = {
  status: 'idle', language: '', translatedText: '', translatedSubject: null, detectedLanguage: '', error: ''
};

/**
 * Message Viewer Component
 *
 * A reusable read-only component for displaying message details including:
 * - Developer info panel (for system developers)
 * - AI confidence evaluation with meter and reasoning
 * - Inbound message section (collapsible)
 * - AI Reply section with approved/generated/diff toggle
 *
 * Used in:
 * - message-approval component (detail panel)
 * - message-search component (timeline selection viewer)
 */
@Component({
  standalone: false,
  selector: 'izzy-message-viewer',
  templateUrl: './message-viewer.component.html',
  styleUrls: ['./message-viewer.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.edit-companion]': 'editCompanion' }
})
export class MessageViewerComponent implements OnChanges, AfterViewInit {

  @Input() message: ChannelMessageEntityExtendedClient | null = null;
  @Input() isSystemDeveloper: boolean = false;
  @Input() isRescoring: boolean = false;
  @Input() canRescore: boolean = false;
  @Input() isReprocessing: boolean = false;
  /** Owning organization ID — used for deep-linking to Credentials from error actions. */
  @Input() organizationId: string | null = null;
  /**
   * Edit-mode companion: render only the "Original message" card and the internal-notes card —
   * no tabs, draft, or analysis. Lets the Review & Approve editor keep the customer's email and
   * the staff notes in view while a reply is edited.
   */
  @Input() editCompanion: boolean = false;

  @Output() openRecord = new EventEmitter<{ EntityName: string; RecordPKey: CompositeKey }>();
  @Output() openAgentRunRecord = new EventEmitter<string>();
  @Output() rescoreRequested = new EventEmitter<void>();
  /** Fires after a staff note is saved on the current message, with the new total (drives the list-card badge). */
  @Output() noteAdded = new EventEmitter<{ messageId: string; noteCount: number }>();

  // Reference to scroll container for resetting scroll position
  @ViewChild('tabContent') tabContentRef!: ElementRef<HTMLElement>;

  /** Drawer used to preview clicked attachments. Populated when user clicks a chip. */
  @ViewChild('attachmentDrawer') attachmentDrawer?: AttachmentDrawerComponent;

  constructor(
    private sanitizer: DomSanitizer,
    private navigationService: NavigationService,
    private cdr: ChangeDetectorRef,
    private translationService: IzzyTranslationService
  ) {}

  // ========== Translate view (read-only translation for staff) ==========
  protected readonly translationLanguages = IzzyTranslationService.LANGUAGES;
  /** Language selected in the picker; seeded from the last pick in this browser (default English). */
  protected translateLanguage: string = IzzyTranslationService.DEFAULT_LANGUAGE;
  protected inboundTranslation: PaneTranslation = { ...IDLE_TRANSLATION };
  protected draftTranslation: PaneTranslation = { ...IDLE_TRANSLATION };

  private paneState(pane: TranslationPane): PaneTranslation {
    return pane === 'inbound' ? this.inboundTranslation : this.draftTranslation;
  }

  private setPaneState(pane: TranslationPane, state: PaneTranslation): void {
    if (pane === 'inbound') {
      this.inboundTranslation = state;
    } else {
      this.draftTranslation = state;
    }
    this.cdr.markForCheck();
  }

  private resetTranslations(): void {
    this.inboundTranslation = { ...IDLE_TRANSLATION };
    this.draftTranslation = { ...IDLE_TRANSLATION };
  }

  /** The inbound pane can be translated whenever the message has inbound text. */
  protected get canTranslateInbound(): boolean {
    return !!this.message?.MessageContent?.trim();
  }

  /**
   * Which reply column the draft card is currently showing, mirroring the template's own
   * precedence for each view mode. Null in Diff mode (two texts, nothing to translate) and when
   * there is no draft at all.
   */
  protected get currentDraftField(): TranslatableField | null {
    const m = this.message;
    if (!m) return null;
    if (this.isPending) {
      if (this.replyViewMode === 'diff') return null;
      if (this.replyViewMode === 'generated') return m.GeneratedReplyContent ? 'GeneratedReplyContent' : null;
      return m.ApprovedReplyContent ? 'ApprovedReplyContent' : m.GeneratedReplyContent ? 'GeneratedReplyContent' : null;
    }
    if (this.isClosed) {
      if (this.closedReplyViewMode === 'diff') return null;
      if (this.closedReplyViewMode === 'generated') return m.GeneratedReplyContent ? 'GeneratedReplyContent' : null;
      if (this.closedReplyViewMode === 'approved') return m.ApprovedReplyContent ? 'ApprovedReplyContent' : null;
      return m.SentContent ? 'SentContent' : m.ApprovedReplyContent ? 'ApprovedReplyContent' : m.GeneratedReplyContent ? 'GeneratedReplyContent' : null;
    }
    return null;
  }

  protected get canTranslateDraft(): boolean {
    return this.currentDraftField !== null;
  }

  private currentPaneText(pane: TranslationPane): string {
    const m = this.message;
    if (!m) return '';
    if (pane === 'inbound') return m.MessageContent || '';
    switch (this.currentDraftField) {
      case 'GeneratedReplyContent': return m.GeneratedReplyContent || '';
      case 'ApprovedReplyContent': return m.ApprovedReplyContent || '';
      case 'SentContent': return m.SentContent || '';
      default: return '';
    }
  }

  /** Header button: opens the language picker, or returns to the original when a translation is shown. */
  protected toggleTranslate(pane: TranslationPane): void {
    const state = this.paneState(pane);
    if (state.status === 'shown' || state.status === 'error') {
      this.setPaneState(pane, { ...IDLE_TRANSLATION });
      return;
    }
    if (state.status === 'loading') return;
    if (state.status === 'picking') {
      this.setPaneState(pane, { ...IDLE_TRANSLATION });
      return;
    }
    this.translateLanguage = this.translationService.lastLanguage;
    this.setPaneState(pane, { ...IDLE_TRANSLATION, status: 'picking' });
  }

  protected cancelTranslate(pane: TranslationPane): void {
    this.setPaneState(pane, { ...IDLE_TRANSLATION });
  }

  /** Runs the translation for the pane in the picked language; a result for a message we've moved off is dropped. */
  protected async runTranslate(pane: TranslationPane): Promise<void> {
    const message = this.message;
    const field: TranslatableField | null = pane === 'inbound' ? 'MessageContent' : this.currentDraftField;
    const language = this.translateLanguage;
    if (!message || !field || !language) return;

    const messageId = message.ID;
    const text = this.currentPaneText(pane);
    this.translationService.lastLanguage = language;
    this.setPaneState(pane, { ...IDLE_TRANSLATION, status: 'loading', language });

    const result = await this.translationService.translate(messageId, field, language, text);
    if (this.message?.ID !== messageId) return; // stale — user moved on

    if (!result.success || !result.translatedText) {
      this.setPaneState(pane, { ...IDLE_TRANSLATION, status: 'error', language, error: result.error || 'Translation failed.' });
      return;
    }
    this.setPaneState(pane, {
      status: 'shown',
      language,
      translatedText: result.translatedText,
      translatedSubject: result.translatedSubject ?? null,
      detectedLanguage: result.detectedLanguage || 'Unknown',
      error: ''
    });
  }

  /** Text for the inbound body: the translation while one is shown, else the original. */
  protected get inboundDisplayContent(): string | null {
    return this.inboundTranslation.status === 'shown' ? this.inboundTranslation.translatedText : (this.message?.MessageContent ?? null);
  }

  /** Subject for the inbound card: translated when the model returned one, else the original. */
  protected get inboundDisplaySubject(): string | null {
    if (this.inboundTranslation.status === 'shown' && this.inboundTranslation.translatedSubject) {
      return this.inboundTranslation.translatedSubject;
    }
    return this.message?.Subject ?? null;
  }

  // ========== Export PDF (plans/prd-pdf-export.md) ==========
  protected isExportingPdf = false;
  protected exportPdfError = '';
  /** Export dialog: one choice — include staff internal notes (default OFF; exports often leave the org). */
  protected showExportDialog = false;
  protected exportIncludeInternalNotes = false;

  protected openExportDialog(): void {
    if (!this.message) return;
    this.exportIncludeInternalNotes = false;
    this.exportPdfError = '';
    this.showExportDialog = true;
    this.cdr.markForCheck();
  }

  protected closeExportDialog(): void {
    if (this.isExportingPdf) return;
    this.showExportDialog = false;
    this.cdr.markForCheck();
  }

  /**
   * Export this message as a PDF via the server-side Export Message PDF action (the browser
   * can't render one). The action returns the document base64-encoded; skipActionLog keeps
   * the multi-MB payload out of ActionExecutionLog (the action leaves a compact server-side
   * log line instead).
   */
  protected async exportPdf(): Promise<void> {
    if (!this.message || this.isExportingPdf) return;
    this.isExportingPdf = true;
    this.exportPdfError = '';
    this.cdr.markForCheck();
    try {
      const rv = new RunView();
      const actionRes = await rv.RunView<{ ID: string }>({
        EntityName: 'MJ: Actions',
        ExtraFilter: `Name = 'Export Message PDF'`,
        Fields: ['ID'],
        MaxRows: 1
      });
      const actionId = (actionRes.Success ? actionRes.Results : [])[0]?.ID;
      if (!actionId) {
        this.exportPdfError = 'Export action is not installed.';
        return;
      }
      const client = new GraphQLActionClient(Metadata.Provider as GraphQLDataProvider);
      const result = await client.RunAction(actionId, [
        { Name: 'ChannelMessageID', Value: this.message.ID, Type: 'Input' },
        { Name: 'IncludeInternalNotes', Value: this.exportIncludeInternalNotes ? 'true' : 'false', Type: 'Input' },
      ] as never, true);
      if (!result?.Success) {
        this.exportPdfError = result?.Message || 'Failed to export the message.';
        return;
      }
      // ActionResult types Result as the result-code entity, but the GraphQL client actually
      // parses the action's Output params (ResultData JSON) into it — narrow via a guard.
      // MJServer's resolver runs the output array through CopyScalarsAndArrays, which turns an
      // array into an index-keyed OBJECT ({"0": {...}}), so accept both shapes.
      const raw: unknown = result.Result;
      const candidates: unknown[] = Array.isArray(raw)
        ? raw
        : raw && typeof raw === 'object' ? Object.values(raw) : [];
      const outputs = candidates.filter((o): o is { Name: string; Value: unknown } =>
        !!o && typeof o === 'object' && typeof (o as { Name?: unknown }).Name === 'string');
      const base64 = outputs.find(o => o.Name === 'PDFBase64')?.Value;
      const fileName = outputs.find(o => o.Name === 'FileName')?.Value;
      if (typeof base64 !== 'string' || base64.length === 0) {
        this.exportPdfError = 'Export returned no document.';
        return;
      }
      DownloadBase64File(base64, typeof fileName === 'string' && fileName ? fileName : 'Message.pdf');
      this.showExportDialog = false;
    } catch (error) {
      this.exportPdfError = error instanceof Error ? error.message : 'Failed to export the message.';
    } finally {
      this.isExportingPdf = false;
      this.cdr.markForCheck();
    }
  }

  // ========== Forward dialog (staff-initiated forward of the inbound email) ==========
  protected showForwardDialog = false;
  protected forwardTo = '';
  protected forwardCc = '';
  protected forwardComment = '';
  protected isForwarding = false;
  protected forwardError = '';
  protected forwardSuccess = false;

  /** Forward is offered only for real inbound provider messages (something exists to forward). */
  protected get canForward(): boolean {
    return !!this.message?.ExternalSystemRecordID && !!this.message?.MessageContent;
  }

  /**
   * True when this record IS a staff forward (OutboundType='Forward') — such records have no
   * inbound half and no AI reply, so the viewer renders a dedicated forward layout instead of
   * the misleading inbound + AI-reply framing.
   */
  protected get isForwardRecord(): boolean {
    return this.message?.OutboundType === 'Forward';
  }

  /**
   * Staff-originated row (Manual Messages, #289): compose ('New') or follow-up ('Reply')
   * created born-Pending by staff — no inbound half, no AI draft. Rendered with its own
   * framing (like forwards) so it can't masquerade as a member message or an Izzy reply.
   */
  protected get isStaffRecord(): boolean {
    return !!this.message?.IsStaffOriginated;
  }

  protected get staffRecordLabel(): string {
    return this.message?.OutboundType === 'New' ? 'Staff-composed message' : 'Staff follow-up reply';
  }

  /** The staff row's outgoing body: what was actually sent, else the pending draft content. */
  protected get staffRecordContent(): string {
    return this.message?.SentContent || this.message?.ApprovedReplyContent || '';
  }

  protected openForwardDialog(): void {
    this.forwardTo = '';
    this.forwardCc = '';
    this.forwardComment = '';
    this.forwardError = '';
    this.forwardSuccess = false;
    this.showForwardDialog = true;
    this.cdr.markForCheck();
  }

  protected closeForwardDialog(): void {
    if (this.isForwarding) return;
    this.showForwardDialog = false;
    this.cdr.markForCheck();
  }

  /**
   * Invokes the server-side Forward Channel Message action (the browser cannot run a
   * communication provider). Suppression and the recipient cap are applied server-side;
   * refusals surface as the action's error message.
   */
  protected async submitForward(): Promise<void> {
    if (!this.message || !this.forwardTo.trim() || this.isForwarding) return;
    this.isForwarding = true;
    this.forwardError = '';
    this.cdr.markForCheck();
    try {
      const rv = new RunView();
      const actionRes = await rv.RunView<{ ID: string }>({
        EntityName: 'MJ: Actions',
        ExtraFilter: `Name = 'Forward Channel Message'`,
        Fields: ['ID'],
        MaxRows: 1
      });
      const actionId = (actionRes.Success ? actionRes.Results : [])[0]?.ID;
      if (!actionId) {
        this.forwardError = 'Forward action is not installed.';
        return;
      }
      const client = new GraphQLActionClient(Metadata.Provider as GraphQLDataProvider);
      const result = await client.RunAction(actionId, [
        { Name: 'ChannelMessageID', Value: this.message.ID, Type: 'Input' },
        { Name: 'ToRecipients', Value: this.forwardTo, Type: 'Input' },
        { Name: 'CCRecipients', Value: this.forwardCc, Type: 'Input' },
        { Name: 'Comment', Value: this.forwardComment, Type: 'Input' },
      ] as never);
      if (result?.Success) {
        this.forwardSuccess = true;
      } else {
        this.forwardError = result?.Message || 'Failed to forward the message.';
      }
    } catch (error) {
      this.forwardError = error instanceof Error ? error.message : 'Failed to forward the message.';
    } finally {
      this.isForwarding = false;
      this.cdr.markForCheck();
    }
  }

  // ========== Internal notes (message-level, staff-only) ==========

  private resetNotes(): void {
    this.messageNotes = [];
    this.notesLoading = false;
    this.notesError = null;
    this.noteDraft = '';
    this.isAddingNote = false;
  }

  /**
   * Loads the current message's notes (in the normal layout and in edit-companion mode alike).
   * A response that arrives after the user moved to another message is dropped.
   */
  private async loadMessageNotes(): Promise<void> {
    const messageId = this.message?.ID;
    if (!messageId) return;
    this.notesLoading = true;
    this.notesError = null;
    this.cdr.markForCheck();
    try {
      const rv = new RunView();
      const res = await rv.RunView<MessageNoteRow>({
        EntityName: 'Channel Message Notes',
        ExtraFilter: `ChannelMessageID = '${messageId}'`,
        OrderBy: '__mj_CreatedAt ASC',
        Fields: ['ID', 'Body', 'AuthorEmail', 'AuthorPerson', '__mj_CreatedAt'],
        ResultType: 'simple',
      });
      if (this.message?.ID !== messageId) return; // stale — user moved on
      if (!res.Success) {
        this.notesError = res.ErrorMessage || 'Failed to load notes.';
        return;
      }
      this.messageNotes = res.Results ?? [];
    } catch (error) {
      if (this.message?.ID !== messageId) return;
      console.error('MessageViewer: failed to load internal notes', error);
      this.notesError = 'Failed to load notes.';
    } finally {
      if (this.message?.ID === messageId) {
        this.notesLoading = false;
        this.cdr.markForCheck();
      }
    }
  }

  /**
   * Saves the composer text as a new note on the current message, as the calling user. The
   * server subclass re-derives OrganizationID from the message's channel, stamps the author,
   * and refuses cross-tenant or empty notes — the client value here only satisfies the required
   * column so the save reaches the server. Re-reads afterwards so author name and time come
   * from the server, then tells the parent so the list-card badge can update.
   */
  protected async addNote(): Promise<void> {
    const message = this.message;
    const body = this.noteDraft.trim();
    if (!message || !body || this.isAddingNote) return;
    this.isAddingNote = true;
    this.notesError = null;
    this.cdr.markForCheck();
    try {
      const orgId = IzzyMetadataEngine.Instance.GetChannel(message.ChannelID)?.OrganizationID || this.organizationId;
      if (!orgId) {
        this.notesError = 'Could not determine the organization for this message.';
        return;
      }
      const md = new Metadata();
      const note = await md.GetEntityObject<IzzyChannelMessageNoteEntity>('Channel Message Notes');
      note.NewRecord();
      note.ChannelMessageID = message.ID;
      note.OrganizationID = orgId;
      note.Body = body;
      if (!await note.Save()) {
        this.notesError = note.LatestResult?.Message || 'Failed to add note. Please try again.';
        return;
      }
      this.noteDraft = '';
      await this.loadMessageNotes();
      if (this.message?.ID === message.ID) {
        this.noteAdded.emit({ messageId: message.ID, noteCount: this.messageNotes.length });
      }
    } catch (error) {
      console.error('MessageViewer: failed to add internal note', error);
      this.notesError = 'Failed to add note. See console for details.';
    } finally {
      this.isAddingNote = false;
      this.cdr.markForCheck();
    }
  }

  /** Logged-in user — resolved via the MJ Metadata helper, non-null per MJ contract. */
  protected get currentUser(): UserInfo {
    return new Metadata().CurrentUser;
  }

  /** Default environment ID. Single environment in current deployment. */
  protected readonly environmentId: string = DEFAULT_ENVIRONMENT_ID;

  /** Bridge from <app-message-attachments> chip click to <app-attachment-drawer>. */
  protected onAttachmentOpen(event: { chip: MessageAttachmentChip; index: number; chips: MessageAttachmentChip[] }): void {
    if (this.attachmentDrawer) {
      this.attachmentDrawer.openWith(event.chips, event.index);
    }
  }

  ngAfterViewInit(): void {
    // Initial setup if needed
  }

  // ========== Section Collapse State ==========
  protected isDevPanelCollapsed: boolean = true;
  protected isErrorPanelCollapsed: boolean = false; // Show errors expanded by default
  protected isInboundCollapsed: boolean = false;
  protected isReplyCollapsed: boolean = false;
  protected isSentContentCollapsed: boolean = false;
  protected isReasoningExpanded: boolean = false;
  protected isSkipReasoningExpanded: boolean = false;
  protected isAuditTrailExpanded: boolean = false;

  // ========== Reply View Mode ==========
  protected replyViewMode: 'approved' | 'generated' | 'diff' = 'approved';
  protected closedReplyViewMode: 'sent' | 'approved' | 'generated' | 'diff' = 'sent';
  protected diffHtml: SafeHtml = '';

  // ========== Tab State ==========
  protected activeDetailsTab: 'message' | 'ai-analysis' | 'timeline' | 'developer' | 'agent-run' = 'message';

  // ========== Agent Run Tab State ==========
  protected agentRunPayload: IzzyPayload | null = null;
  protected agentRunLoading: boolean = false;
  protected agentRunError: string | null = null;
  protected agentRunLoaded: boolean = false;

  // ========== Rescore State ==========
  /** Previous isRescoring value, so we can spot the true → false completion edge. */
  private wasRescoring: boolean = false;

  // ========== Quality Feedback State ==========
  protected feedbackSaving: boolean = false;
  protected feedbackSaved: boolean = false;
  private feedbackSavedTimer: ReturnType<typeof setTimeout> | null = null;

  // ========== Error Detail Expansion State ==========
  protected isErrorDetailExpanded: boolean = false;

  // ========== Internal Notes State (message-level, staff-only) ==========
  protected messageNotes: MessageNoteRow[] = [];
  protected notesLoading: boolean = false;
  protected notesError: string | null = null;
  protected noteDraft: string = '';
  protected isAddingNote: boolean = false;

  ngOnChanges(changes: SimpleChanges): void {
    // Detect rescore completion: isRescoring transitions true → false
    if (changes['isRescoring'] && this.wasRescoring && !this.isRescoring) {
      // Auto-switch to AI Analysis tab so user sees the results. The staggered category-card
      // animation now lives in <izzy-ai-analysis>, which watches the same isRescoring transition.
      this.activeDetailsTab = 'ai-analysis';
      this.cdr.markForCheck();
    }
    this.wasRescoring = this.isRescoring;

    if (changes['message'] && this.message) {
      // Reset state when message changes
      this.isReasoningExpanded = false;
      this.isSkipReasoningExpanded = false;
      this.isInboundCollapsed = false;
      this.isReplyCollapsed = false;
      this.replyViewMode = 'approved';
      // Default to the Sent view only when something was actually sent (or
      // approved) — an escalated/never-sent message would render an empty
      // draft body otherwise.
      this.closedReplyViewMode = (this.message.SentContent || this.message.ApprovedReplyContent)
        ? 'sent' : 'generated';
      this.activeDetailsTab = 'message';

      // Translations belong to one message's text — drop them (the service keeps its cache).
      this.resetTranslations();

      // Close any open attachment drawer - it holds the previous message's chips.
      this.attachmentDrawer?.close();

      // Reset feedback state
      this.feedbackSaving = false;
      this.feedbackSaved = false;
      if (this.feedbackSavedTimer) {
        clearTimeout(this.feedbackSavedTimer);
        this.feedbackSavedTimer = null;
      }

      // Reset agent run state for lazy loading
      this.agentRunPayload = null;
      this.agentRunLoading = false;
      this.agentRunError = null;
      this.agentRunLoaded = false;

      // Internal notes belong to one message — drop the previous message's and fetch this one's.
      this.resetNotes();
      void this.loadMessageNotes();

      // Reset scroll position when switching messages
      this.resetScrollPosition();

      // Compute diff if message has ApprovedReplyContent
      if (this.message.ApprovedReplyContent) {
        this.computeHtmlDiff(
          this.message.GeneratedReplyContent || '',
          this.message.ApprovedReplyContent || ''
        );
      }
    }
  }

  /**
   * Reset scroll position to top when switching messages
   */
  private resetScrollPosition(): void {
    // Use setTimeout to ensure DOM has updated
    setTimeout(() => {
      if (this.tabContentRef?.nativeElement) {
        this.tabContentRef.nativeElement.scrollTop = 0;
      }
      // Also reset scroll on the tabs container parent
      const tabsContainer = this.tabContentRef?.nativeElement?.closest('.tabs-container');
      if (tabsContainer) {
        tabsContainer.scrollTop = 0;
      }
    }, 0);
  }

  // ========== Section Toggle Methods ==========

  toggleInboundCollapse(): void {
    this.isInboundCollapsed = !this.isInboundCollapsed;
  }

  toggleReplyCollapse(): void {
    this.isReplyCollapsed = !this.isReplyCollapsed;
  }

  // ========== View State Helpers ==========

  /**
   * Check if message is in a pending state (needs action)
   */
  get isPending(): boolean {
    return this.message?.ApprovalStatus === 'Pending';
  }

  /**
   * Check if message is closed (approved, rejected, or not required)
   */
  get isClosed(): boolean {
    return !this.isPending && this.message?.ApprovalStatus !== null;
  }

  /**
   * Get the reply content to display (approved if available, otherwise generated)
   */
  get displayReplyContent(): string | null {
    if (!this.message) return null;
    // Fall back to SentContent so a manually-composed reply (sent without an AI-generated/approved
    // draft) still renders in the closed "Reply" section instead of showing "No reply generated".
    return this.message.ApprovedReplyContent || this.message.GeneratedReplyContent || this.message.SentContent || null;
  }

  /**
   * Set the active details tab
   */
  setDetailsTab(tab: 'message' | 'ai-analysis' | 'timeline' | 'developer' | 'agent-run'): void {
    this.activeDetailsTab = tab;

    // Lazy load agent run data when tab is selected
    if (tab === 'agent-run' && !this.agentRunLoaded && this.message?.AIAgentRunID) {
      this.loadAgentRunPayload();
    }
  }

  /**
   * Set the closed reply view mode (for viewing sent messages)
   */
  setClosedReplyViewMode(mode: 'sent' | 'approved' | 'generated' | 'diff'): void {
    this.closedReplyViewMode = mode;
    // A translation belongs to the text that was showing; switching views goes back to the original.
    this.draftTranslation = { ...IDLE_TRANSLATION };
    if (mode === 'diff' && this.message) {
      // Diff compares Approved vs Generated (user edits only, not signature additions)
      this.computeHtmlDiff(
        this.message.GeneratedReplyContent || '',
        this.message.ApprovedReplyContent || ''
      );
    }
  }

  /**
   * Check if the reply was edited by the user (approved differs from generated)
   * This excludes signature additions which happen after approval
   */
  get wasUserEdited(): boolean {
    if (!this.message) return false;
    return !!this.message.ApprovedReplyContent &&
           !!this.message.GeneratedReplyContent &&
           this.message.ApprovedReplyContent !== this.message.GeneratedReplyContent;
  }

  /**
   * Check if the reply was edited (sent differs from generated)
   * This includes both user edits AND signature additions
   */
  get wasReplyEdited(): boolean {
    if (!this.message) return false;
    const sentOrApproved = this.message.SentContent || this.message.ApprovedReplyContent;
    return !!sentOrApproved && sentOrApproved !== this.message.GeneratedReplyContent;
  }

  /**
   * The draft's disposition, shown as the single state chip on the draft card
   * header. This is the one place the Message tab signals approval state —
   * the old pending/closed status banners are gone.
   */
  protected get draftState(): { label: string; cls: string; icon: string } {
    const m = this.message;
    if (!m) return { label: '', cls: 'state-muted', icon: 'fa-file-lines' };
    switch (m.ApprovalStatus) {
      case 'Pending':
        return { label: 'Pending review', cls: 'state-pending', icon: 'fa-hourglass-half' };
      case 'Escalated':
        return { label: 'Escalated', cls: 'state-escalated', icon: 'fa-triangle-exclamation' };
      case 'Approved':
        if (m.ErrorType && !m.SentAt && this.isSendError(m.ErrorType)) {
          return { label: 'Approved · send failed', cls: 'state-error', icon: 'fa-triangle-exclamation' };
        }
        if (m.AIAutoApproved) {
          return { label: m.SentAt ? 'Auto-approved · sent' : 'Auto-approved', cls: 'state-approved', icon: 'fa-bolt' };
        }
        return { label: m.SentAt ? 'Approved · sent' : 'Approved', cls: 'state-approved', icon: 'fa-paper-plane' };
      case 'Rejected':
        return { label: 'Dismissed', cls: 'state-rejected', icon: 'fa-ban' };
      case 'Not Required':
        return { label: 'No reply required', cls: 'state-muted', icon: 'fa-minus-circle' };
      default:
        return { label: m.ApprovalStatus ?? 'Draft', cls: 'state-muted', icon: 'fa-file-lines' };
    }
  }

  /** Most meaningful timestamp for the draft card header. */
  protected get draftTimestamp(): Date | string | null {
    const m = this.message;
    if (!m) return null;
    return m.SentAt || m.ApprovedAt || m.AIEvaluatedAt || null;
  }

  /**
   * Lazy load the agent run payload data
   */
  private async loadAgentRunPayload(): Promise<void> {
    if (!this.message?.AIAgentRunID || this.agentRunLoaded) return;

    this.agentRunLoading = true;
    this.agentRunError = null;
    // Trigger change detection to show loading spinner
    this.cdr.markForCheck();

    try {
      const md = new Metadata();
      const agentRun = await md.GetEntityObject<MJAIAgentRunEntity>("MJ: AI Agent Runs");
      const result = await agentRun.Load(this.message.AIAgentRunID);
      if (result && agentRun) {
        // Get the FinalPayload which contains the final payload JSON
        const payloadJson = agentRun.FinalPayload;
        if (payloadJson) {
          // Parse the JSON into IzzyPayload type
          try {
            this.agentRunPayload = JSON.parse(payloadJson) as IzzyPayload;
          } catch {
            // If it's not valid JSON, show error
            this.agentRunPayload = null;
            this.agentRunError = 'Failed to parse payload data as JSON.';
          }
        } else {
          this.agentRunPayload = null;
          this.agentRunError = 'No payload data available for this agent run.';
        }
      } else {
        this.agentRunError = 'Agent run record not found.';
      }
    } catch (error) {
      console.error('Error loading agent run payload:', error);
      this.agentRunError = `Failed to load agent run data: ${error instanceof Error ? error.message : 'Unknown error'}`;
    } finally {
      this.agentRunLoading = false;
      this.agentRunLoaded = true;
      // Trigger change detection since we're using OnPush strategy
      this.cdr.markForCheck();
    }
  }

  // ========== Reply View Mode ==========

  setReplyViewMode(mode: 'approved' | 'generated' | 'diff'): void {
    this.replyViewMode = mode;
    // A translation belongs to the text that was showing; switching views goes back to the original.
    this.draftTranslation = { ...IDLE_TRANSLATION };
    if (mode === 'diff' && this.message) {
      this.computeHtmlDiff(
        this.message.GeneratedReplyContent || '',
        this.message.ApprovedReplyContent || ''
      );
    }
  }

  private computeHtmlDiff(original: string, modified: string): void {
    try {
      const diff = HtmlDiff.execute(original, modified);
      this.diffHtml = this.sanitizer.bypassSecurityTrustHtml(diff);
    } catch (error) {
      console.error('MessageViewer: Error computing HTML diff:', error);
      this.diffHtml = this.sanitizer.bypassSecurityTrustHtml(modified);
    }
  }

  // ========== Navigation ==========

  openMessageRecord(): void {
    if (!this.message) return;
    this.openRecord.emit({
      EntityName: 'Channel Messages',
      RecordPKey: CompositeKey.FromID(this.message.ID)
    });
  }

  openAgentRun(agentRunId: string): void {
    if (!agentRunId) return;
    this.openAgentRunRecord.emit(agentRunId);
    this.navigationService.OpenEntityRecord(
      'MJ: AI Agent Runs',
      CompositeKey.FromID(agentRunId)
    );
  }

  // ========== Utility Methods ==========

  renderMarkdown(content: string | null | undefined): SafeHtml {
    if (!content) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }
    // Replace &nbsp; with regular spaces to allow proper word wrapping
    // Quill editor inserts &nbsp; which prevents text from wrapping normally
    const normalizedContent = content.replace(/&nbsp;/g, ' ');
    let html = marked.parse(normalizedContent, { async: false }) as string;
    // Strip inline color and background styles so email content adapts to dark mode
    html = html.replace(/\b(color|background|background-color)\s*:\s*[^;"}]+;?/gi, '');
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  formatDate(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  formatRelativeTime(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  }

  truncateText(text: string, maxLength: number = 100): string {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  // ========== Status Classes ==========

  getApprovalStatusClass(status: string | null): string {
    switch (status) {
      case 'Pending': return 'approval-pending';
      case 'Approved': return 'approval-approved';
      case 'Rejected': return 'approval-rejected';
      case 'Not Required': return 'approval-not-required';
      case 'Escalated': return 'approval-escalated';
      default: return '';
    }
  }

  getGenerationStatusClass(status: string): string {
    switch (status) {
      case 'Read': return 'status-read';
      case 'Creating Reply': return 'status-creating-reply';
      case 'Generated': return 'status-generated';
      case 'Skipped': return 'status-skipped';
      case 'Failed': return 'status-failed';
      default: return '';
    }
  }

  getConfidenceScoreClass(score: number | null): string {
    if (score === null || score === undefined) return '';
    if (score >= 85) return 'confidence-high';
    if (score >= 60) return 'confidence-medium';
    return 'confidence-low';
  }

  // ========== Skip Category Helpers ==========

  /**
   * Get icon class for skip category
   */
  getSkipIconClass(category: string | null): string {
    if (!category) return 'fa-circle-xmark';
    switch (category.toLowerCase()) {
      case 'spam': return 'fa-ban';
      case 'automated-message': return 'fa-robot';
      case 'prank': return 'fa-face-grin-tongue-wink';
      case 'phishing': return 'fa-fish';
      case 'legal-advice': return 'fa-scale-balanced';
      case 'financial-dispute': return 'fa-money-bill-transfer';
      case 'explicit-human-request': return 'fa-user-check';
      case 'urgent-sensitive-personal': return 'fa-user-shield';
      case 'urgent-sensitive-business': return 'fa-building-shield';
      case 'policy-exception': return 'fa-file-contract';
      case 'complex-technical': return 'fa-microchip';
      case 'password-reset-failure': return 'fa-key';
      case 'marketing-speaker': return 'fa-bullhorn';
      case 'inappropriate': return 'fa-triangle-exclamation';
      case 'out-of-scope': return 'fa-circle-question';
      case 'model-content-blocked': return 'fa-shield-halved';
      default: return 'fa-circle-xmark';
    }
  }

  /**
   * Get CSS class for skip category badge
   */
  getSkipCategoryClass(category: string | null): string {
    if (!category) return 'skip-unknown';
    switch (category.toLowerCase()) {
      case 'spam':
      case 'phishing':
      case 'prank':
        return 'skip-garbage';
      case 'automated-message':
        return 'skip-automated';
      case 'legal-advice':
      case 'financial-dispute':
      case 'policy-exception':
        return 'skip-requires-human';
      case 'explicit-human-request':
      case 'urgent-sensitive-personal':
      case 'urgent-sensitive-business':
        return 'skip-sensitive';
      case 'complex-technical':
      case 'password-reset-failure':
      case 'model-content-blocked':
        return 'skip-technical';
      case 'marketing-speaker':
      case 'inappropriate':
      case 'out-of-scope':
        return 'skip-other';
      default:
        return 'skip-unknown';
    }
  }

  /**
   * Format skip category for display (convert kebab-case to Title Case)
   */
  formatSkipCategory(category: string | null): string {
    if (!category) return 'Unknown';
    if (category.toLowerCase() === 'model-content-blocked') return 'AI Model Refused';
    return category
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // ========== Error Type Helpers ==========

  /**
   * Format error type for display (convert PascalCase to Title Case with spaces)
   */
  formatErrorType(errorType: string | null): string {
    if (!errorType) return 'Unknown';
    // Insert space before each capital letter and trim
    return errorType
      .replace(/([A-Z])/g, ' $1')
      .trim();
  }

  /**
   * Get CSS class for error type styling
   */
  getErrorTypeClass(errorType: string | null): string {
    if (!errorType) return 'error-unknown';
    switch (errorType) {
      case 'AgentFailure':
        return 'error-agent';
      case 'SendFailure':
        return 'error-send';
      case 'CredentialFailure':
        return 'error-credential';
      case 'ProviderNotFound':
        return 'error-provider';
      case 'ValidationFailure':
        return 'error-validation';
      case 'ChannelConfigFailure':
        return 'error-config';
      case 'Timeout':
        return 'error-timeout';
      case 'RateLimited':
        return 'error-rate-limited';
      case 'Other':
      default:
        return 'error-other';
    }
  }

  /**
   * Determines if an error type is a send-related error (vs AI generation error)
   */
  isSendError(errorType: string | null): boolean {
    if (!errorType) return false;
    const sendErrors = ['SendFailure', 'CredentialFailure', 'Timeout',
                        'RateLimited', 'ProviderNotFound', 'ChannelConfigFailure', 'ValidationFailure'];
    return sendErrors.includes(errorType);
  }

  /**
   * Builds the friendly error object for the currently-displayed message.
   * Returns null when there is no error to display.
   */
  getFriendlyError(): FriendlyError | null {
    if (!this.message?.ErrorType && !this.message?.ErrorMessage) return null;
    return categorizeError(this.message.ErrorType, this.message.ErrorMessage);
  }

  toggleErrorDetail(): void {
    this.isErrorDetailExpanded = !this.isErrorDetailExpanded;
  }

  /** Show more / Show less on the escalation reason card (state resets per message in ngOnChanges). */
  toggleSkipReasoning(): void {
    this.isSkipReasoningExpanded = !this.isSkipReasoningExpanded;
  }

  /**
   * Whether the reason card needs a Show more toggle: roughly more than four rendered lines.
   * Short reasons render in full with no toggle, so the common case looks exactly as before.
   */
  skipReasonNeedsToggle(text: string | null | undefined): boolean {
    if (!text) return false;
    const lineBreaks = (text.match(/\n/g) || []).length;
    return text.length > 320 || lineBreaks >= 4;
  }

  /**
   * Navigate to the Credentials admin section, filtered to AI category.
   * Used by the "Check AI credentials" deep-link on AI-related errors.
   */
  openAICredentials(): void {
    const params: Record<string, string> = { section: 'credentials', category: 'AI' };
    if (this.organizationId) params['organizationId'] = this.organizationId;
    this.navigationService.OpenNavItemByName('Administration', params);
  }

  // ========== Quality Feedback ==========

  /**
   * Show the feedback bar whenever there is an AI-generated reply to rate.
   */
  get showFeedbackBar(): boolean {
    return !!this.message?.GeneratedReplyContent;
  }

  /**
   * Submit a thumbs-up or thumbs-down quality rating for the AI response.
   * Clicking the active rating again clears it (toggle off).
   */
  async submitQualityFeedback(rating: 'Up' | 'Down'): Promise<void> {
    if (!this.message || this.feedbackSaving) return;

    this.feedbackSaving = true;
    this.feedbackSaved = false;
    this.cdr.markForCheck();

    const previousRating = this.message.QualityFeedback;
    const previousAt = this.message.QualityFeedbackAt;
    const previousBy = this.message.QualityFeedbackByPersonID;

    try {
      const contactId = IzzyMetadataEngine.Instance.CurrentContactID ?? '';
      this.message.SubmitQualityFeedback(rating, contactId);

      const saved = await this.message.Save();
      if (saved) {
        this.feedbackSaved = true;
        if (this.feedbackSavedTimer) clearTimeout(this.feedbackSavedTimer);
        this.feedbackSavedTimer = setTimeout(() => {
          this.feedbackSaved = false;
          this.cdr.markForCheck();
        }, 2000);
      } else {
        // Revert on failure
        this.message.QualityFeedback = previousRating;
        this.message.QualityFeedbackAt = previousAt;
        this.message.QualityFeedbackByPersonID = previousBy;
        console.error('MessageViewer: Failed to save quality feedback');
      }
    } catch (error) {
      // Revert on error
      this.message.QualityFeedback = previousRating;
      this.message.QualityFeedbackAt = previousAt;
      this.message.QualityFeedbackByPersonID = previousBy;
      console.error('MessageViewer: Error saving quality feedback:', error);
    } finally {
      this.feedbackSaving = false;
      this.cdr.markForCheck();
    }
  }
}

/**
 * Utility function to prevent tree-shaking
 */
export function LoadMessageViewerComponent(): void {
  // Intentionally empty
}
